import { yupResolver } from '@hookform/resolvers/yup';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import React, { useContext, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useHistory, useParams } from 'react-router-dom';
import * as yup from 'yup';

import { createJumpstartCompany } from '../../../services/jumpstart';
import { UserContext } from '../../context/UserContext';
import { TextButton } from '../../global/components/Button/Button';
import TextInput from '../../global/components/TextField/TextField';
import ColumnCard from '../../global/layouts/ColumnCard/ColumnCard';
import MainContainer from '../../global/layouts/MainContainer/MainContainer';
import { FormSection, StyledButton } from '../Profile/styles';

import type { Control, FieldValues } from 'react-hook-form';

const formSchema = yup
    .object()
    .shape({
        companyName: yup.string().required('Company name is required'),
        location: yup.string().required('Location is required')
    })
    .required();

const LOADING_MESSAGES = [
    'Searching the internet for your company...',
    'Gathering company information...',
    'Analyzing your industry...',
    'Creating your company profile...',
    'Generating job postings...',
    'Crafting compelling job descriptions...',
    'Adding the finishing touches...',
    'Almost there...'
];

export default function JumpstartForm() {
    const { user, setCompany } = useContext(UserContext);
    const { uid } = useParams<{ uid: string }>();
    const history = useHistory();

    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [loadingMessageIndex, setLoadingMessageIndex] = useState(0);

    const {
        handleSubmit,
        formState: { errors },
        control
    } = useForm({
        resolver: yupResolver(formSchema),
        defaultValues: {
            companyName: '',
            location: ''
        }
    });

    useEffect(() => {
        if (isLoading) {
            const interval = setInterval(() => {
                setLoadingMessageIndex(
                    (prev) => (prev + 1) % LOADING_MESSAGES.length
                );
            }, 2500);

            return () => clearInterval(interval);
        }
        return undefined;
    }, [isLoading]);

    const onSubmit = async (data: {
        companyName: string;
        location: string;
    }) => {
        setIsLoading(true);
        setError(null);

        try {
            const result = await createJumpstartCompany({
                user_id: user.id,
                company_name: data.companyName,
                location: data.location
            });
            setCompany(result.company);
            // Redirect to view company page with jumpstart query param
            history.push(`/secure/${uid}/company/${result.company.id}`);
        } catch (err) {
            console.error('Jumpstart failed:', err);
            setError(
                'We encountered an issue generating your company profile. Please try the traditional setup instead.'
            );
            setIsLoading(false);
        }
    };

    const handleTraditionalFlow = () => {
        history.push(`/create/${uid}/business/name`);
    };

    return (
        <MainContainer public fullWidth>
            <ColumnCard closeScreen={false} step={1} screenType={'business'}>
                {isLoading ? (
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            minHeight: '300px',
                            py: 6
                        }}
                    >
                        <CircularProgress size={80} thickness={4} />
                        <Box
                            sx={{
                                mt: 4,
                                textAlign: 'center',
                                minHeight: '60px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}
                        >
                            <Typography variant='h6' color='primary'>
                                {LOADING_MESSAGES[loadingMessageIndex]}
                            </Typography>
                        </Box>
                    </Box>
                ) : (
                    <FormSection onSubmit={handleSubmit(onSubmit)}>
                        <Typography
                            variant='h2'
                            component='h1'
                            fontWeight='400'
                            sx={{ mb: 1 }}
                        >
                            {'Quick Company Setup'}
                        </Typography>
                        <Typography
                            variant='body1'
                            sx={{ mb: 3, color: 'text.secondary' }}
                        >
                            {
                                "We'll automatically gather your company information\nand create sample job postings to get you started\n                            quickly."
                            }
                        </Typography>

                        <TextInput
                            id='companyName'
                            label='Company Name'
                            color='primary'
                            error={!!errors.companyName}
                            errorText={errors.companyName?.message}
                            placeholder='e.g., Acme Corporation'
                            control={control as unknown as Control<FieldValues>}
                        />

                        <TextInput
                            id='location'
                            label='Location'
                            color='primary'
                            error={!!errors.location}
                            errorText={errors.location?.message}
                            placeholder='e.g., San Francisco, CA'
                            control={control as unknown as Control<FieldValues>}
                        />

                        {error && (
                            <Box
                                sx={{
                                    p: 2,
                                    mb: 2,
                                    backgroundColor: 'error.lighter',
                                    borderRadius: 1,
                                    border: '1px solid',
                                    borderColor: 'error.main'
                                }}
                            >
                                <Typography color='error.main'>
                                    {error}
                                </Typography>
                                <TextButton
                                    onClick={handleTraditionalFlow}
                                    sx={{ mt: 1 }}
                                >
                                    {'Switch to Traditional Setup'}
                                </TextButton>
                            </Box>
                        )}

                        <StyledButton
                            type='submit'
                            variant='contained'
                            color='primary'
                        >
                            {'Generate Company Profile'}
                        </StyledButton>

                        <TextButton
                            onClick={handleTraditionalFlow}
                            sx={{ mt: 2, alignSelf: 'center' }}
                        >
                            {'Or set up manually instead'}
                        </TextButton>
                    </FormSection>
                )}
            </ColumnCard>
        </MainContainer>
    );
}
